import { useState } from "react";
import { Link } from "react-router-dom";
import { ChevronDown, Lock, Plus, Check, Sparkles, TrendingUp, Star, ShieldCheck } from "lucide-react";
import { fmtOdds, pct, fairOdds, SPORT_META, CONF_STYLE } from "@/lib/odds";
import { useSlip } from "@/context/SlipContext";
import { MoveChip } from "@/components/MoveChip";
import { FormDots } from "@/components/StandingsTable";

function FormChip({ pick }) {
  const tf = pick.team_form;
  if (!tf || !pick.form_tags?.length) return null;
  const backed = tf.backed === tf.home?.team ? tf.home : tf.away;
  const opp = backed === tf.home ? tf.away : tf.home;
  const hot = pick.form_tags.includes("backing_hot");
  const row = hot ? backed : opp;
  if (!row) return null;
  return (
    <Link to={`/teams/${tf.sport}/${row.slug}`} onClick={(e) => e.stopPropagation()} data-testid={`pick-form-${hot ? "hot" : "cold"}`} className={`flex items-center gap-2 rounded-lg border px-3 py-1.5 text-xs transition-colors ${hot ? "border-orange-500/30 bg-orange-500/5 text-orange-200 hover:bg-orange-500/10" : "border-sky-500/30 bg-sky-500/5 text-sky-200 hover:bg-sky-500/10"}`}>
      <span className="font-bold uppercase tracking-wider">{hot ? "Backing hot" : "Fading cold"}</span>
      <span className="truncate">{row.team} · #{row.rank} · {row.streak}</span>
      <FormDots form={row.form} size="h-1.5 w-1.5" />
    </Link>
  );
}

export function ProbRing({ value, size = 64, locked = false }) {
  const r = (size - 8) / 2;
  const c = 2 * Math.PI * r;
  const p = value ?? 0;
  const color = p >= 0.65 ? "#10B981" : p >= 0.55 ? "#F59E0B" : "#94A3B8";
  return (
    <div className="relative shrink-0" style={{ width: size, height: size }} data-testid="pick-win-probability-badge">
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={size / 2} cy={size / 2} r={r} stroke="#26334D" strokeWidth="5" fill="none" />
        {!locked && <circle cx={size / 2} cy={size / 2} r={r} stroke={color} strokeWidth="5" fill="none" strokeLinecap="round" strokeDasharray={c} strokeDashoffset={c * (1 - p)} className="transition-[stroke-dashoffset] duration-700" />}
      </svg>
      <span className="absolute inset-0 flex items-center justify-center font-mono-num text-sm font-bold" style={{ color: locked ? "#64748B" : color }}>
        {locked ? <Lock size={14} /> : pct(value)}
      </span>
    </div>
  );
}

export function PickCard({ pick, index = 0, compact = false }) {
  const [open, setOpen] = useState(false);
  const { has, toggle } = useSlip();
  const inSlip = has(pick.pick_id);
  const meta = SPORT_META[pick.sport];
  const time = new Date(pick.start_time).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });

  return (
    <article data-testid="pick-card" className={`glass-card fade-up flex min-w-0 flex-col gap-4 overflow-hidden p-5 ${inSlip ? "glow-green !border-emerald-500/60" : ""}`} style={{ animationDelay: `${Math.min(index, 8) * 60}ms` }}>
      <div className="flex items-start gap-4">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="label-caps flex items-center gap-1.5" style={{ color: meta.color }}><span className="h-1.5 w-1.5 rounded-full" style={{ background: meta.color }} />{meta.short}</span>
            <span className="text-xs text-slate-500">· {time}</span>
            {pick.my_team && <span data-testid="pick-my-team" className="flex items-center gap-1 rounded-full bg-amber-500/15 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-amber-300"><Star size={9} className="fill-amber-300" /> Your team</span>}
            {pick.ai_generated && <span className="ml-auto flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider text-cyan-400" data-testid="pick-model"><Sparkles size={10} /> {pick.model || "Claude"}</span>}
          </div>
          {pick.consensus && (
            <span data-testid="pick-consensus" title={pick.consensus.agree ? `${pick.consensus.models.join(" + ")} both back this side` : `Second opinion: ${pick.consensus.second_opinion?.selection || "different side"}`}
              className={`mt-1.5 inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${pick.consensus.agree ? "border-emerald-500/50 bg-emerald-500/15 text-emerald-300" : "border-amber-500/40 bg-amber-500/10 text-amber-300"}`}>
              <ShieldCheck size={10} /> {pick.consensus.agree ? `Consensus · ${Math.round((pick.consensus_score || 0) * 100)}%` : "Models split"}
            </span>
          )}
          <p className="mt-1.5 truncate text-sm text-slate-400" data-testid="pick-matchup"><Link to={`/games/${pick.event_id}`} data-testid="pick-game-link" onClick={(e) => e.stopPropagation()} className="hover:text-emerald-300 hover:underline">{pick.matchup}</Link></p>
          <h3 className="font-display mt-1 text-2xl font-bold leading-none tracking-tight text-white" data-testid="pick-selection">{pick.selection}</h3>
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <span className="rounded bg-[#0B0E14] px-2 py-0.5 text-xs text-slate-400">{pick.market}</span>
            <span className="font-mono-num text-base font-bold text-amber-400" data-testid="pick-odds">{fmtOdds(pick.odds)}{pick.odds_estimated && <span data-testid="pick-odds-est" title="No live tote price on the current racing data plan — rank-based estimate; the model's win probability is the real signal" className="ml-1 align-middle text-[10px] font-semibold uppercase tracking-wider text-slate-500">est.</span>}</span>
            <span data-testid="pick-confidence" className={`rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${CONF_STYLE[pick.confidence]}`}>{pick.confidence}</span>
            <MoveChip movement={pick.movement} spark={!compact} />
            {pick.trend && (
              <span data-testid="pick-trend-chip" title={`${pick.trend.label} ${pick.trend.record} this season`} className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-bold ${pick.trend.pct >= 55 ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-300" : pick.trend.pct <= 45 ? "border-red-500/40 bg-red-500/10 text-red-300" : "border-[#26334D] text-slate-300"}`}>
                <span className="uppercase tracking-wider">{pick.trend.label}</span> <span className="font-mono-num">{pick.trend.record}</span>{pick.trend.pct != null && <span className="font-mono-num text-slate-400">· {pick.trend.pct}%</span>}
              </span>
            )}
          </div>
        </div>
        <div className="flex flex-col items-center gap-1">
          <ProbRing value={pick.win_probability} locked={pick.locked} />
          <span className="text-[10px] uppercase tracking-wider text-slate-500">Win prob</span>
        </div>
      </div>

      {!compact && (
        <>
          <FormChip pick={pick} />
          {pick.edge != null && (pick.odds_estimated ? (
            <div className="flex items-center justify-between rounded-lg bg-[#0B0E14] px-3 py-2 text-xs" data-testid="pick-fair-odds">
              <span className="text-slate-500">Model fair odds <span className="font-mono-num font-bold text-emerald-400">{fmtOdds(fairOdds(pick.win_probability))}</span></span>
              <span className="text-slate-500">Est. implied <span className="font-mono-num text-slate-300">{pct(pick.implied_prob)}</span></span>
            </div>
          ) : (
            <div className="flex items-center justify-between rounded-lg bg-[#0B0E14] px-3 py-2 text-xs">
              <span className="text-slate-500">Implied <span className="font-mono-num text-slate-300">{pct(pick.implied_prob)}</span></span>
              <span className="text-slate-500">Model edge <span data-testid="pick-edge" className={`font-mono-num font-bold ${pick.edge >= 0 ? "text-emerald-400" : "text-red-400"}`}>{pick.edge >= 0 ? "+" : ""}{(pick.edge * 100).toFixed(1)}%</span></span>
            </div>
          ))}
          {pick.movement && pick.movement.direction !== "flat" && (
            <p data-testid="pick-clv-note" className={`flex items-start gap-1.5 rounded-lg border px-3 py-2 text-xs leading-relaxed ${pick.movement.direction === "in" ? "border-red-500/25 bg-red-500/5 text-red-200/90" : "border-emerald-500/25 bg-emerald-500/5 text-emerald-200/90"}`}>
              <TrendingUp size={13} className="mt-0.5 shrink-0" /> {pick.movement.clv.text}
            </p>
          )}
          <div>
            <button data-testid="pick-ai-reasoning-toggle" onClick={() => setOpen((o) => !o)} className="flex w-full items-center justify-between text-left text-xs font-semibold uppercase tracking-wider text-slate-400 transition-colors hover:text-emerald-400">
              AI Reasoning <ChevronDown size={14} className={`transition-transform duration-200 ${open ? "rotate-180" : ""}`} />
            </button>
            {open && (
              <div className="fade-up mt-3 space-y-3" data-testid="pick-ai-reasoning">
                <p className="text-sm leading-relaxed text-slate-300">{pick.reasoning}</p>
                {pick.locked ? (
                  <p className="text-xs text-amber-400/90">Key factors and win probability are available on Basic & Premium.</p>
                ) : (
                  <ul className="flex flex-wrap gap-2">{pick.key_factors.map((f) => <li key={f} className="rounded-full border border-[#26334D] px-2.5 py-1 text-xs text-slate-300">{f}</li>)}</ul>
                )}
              </div>
            )}
          </div>
        </>
      )}

      <button data-testid="pick-add-to-parlay-btn" onClick={() => toggle(pick)} className={inSlip ? "btn-ghost !border-emerald-500/60 !text-emerald-300 w-full" : "btn-primary w-full"}>
        {inSlip ? <><Check size={16} /> In Parlay Slip</> : <><Plus size={16} /> Add to Parlay</>}
      </button>
    </article>
  );
}
